import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { Box, Divider, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Alert } from "Components/_Shared/Alert";
import { Modal } from "Components/_Shared/BlModal";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { DialogModal } from "Components/_Shared/DialogModal";
import { Empty } from "Components/_Shared/Empty";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import {
  getGetAnalysisQueryKey,
  getListCollaboratorsQueryKey,
  useListCollaborators,
  useRemoveCollaborator,
  useShareAnalysis,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { type AgentSummary, ShareAnalysisNotFoundErrorCode } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type ReactNode, useContext, useId, useState } from "react";
import { toast } from "sonner";
import { SHARING_ADVISORS_MAX_COUNT } from "../../constants";
import { AnalysisDetailContext } from "../AnalysisDetailContext";
import { SharingAdvisorItem } from "./SharingAdvisorItem";
import { SharingAdvisorSearch } from "./SharingAdvisorSearch";
import { SharingCurrentAdvisor } from "./SharingCurrentAdvisor";
import { TakeBackConfirmDialog } from "./TakeBackConfirmDialog";
import { getAgentFullName } from "./utils";

type Props = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly analysisName: string;
  readonly version: number;
  readonly sharedWithAgent: AgentSummary | null;
};

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px">
    {children}
  </Typography>
);

export const AnalysisSharingModal = ({ open, onClose, analysisName, version, sharedWithAgent }: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const queryClient = useQueryClient();
  const titleId = useId();

  const { data: collaboratorList, isLoading, isError } = useListCollaborators({ query: { enabled: open } });
  const advisors = collaboratorList?.data ?? [];

  const { mutate: removeCollaborator, isPending: isRemoving } = useRemoveCollaborator({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCollaboratorsQueryKey() });
      },
      onError: () => {
        toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
      },
    },
  });

  const { mutate: shareAnalysis, isPending: isSharing } = useShareAnalysis({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetAnalysisQueryKey(analysisId, versionId) });
        toast.success(t(Resources.AnalysisDetail.Sharing.Toast.Shared));
      },
      onError: (error, { data }) => {
        if (error?.code === ShareAnalysisNotFoundErrorCode.nsure_agent_not_found) {
          const advisor = advisors.find(({ userGuid }) => userGuid === data.userGuid);
          toast.error(
            t(Resources.AnalysisDetail.Sharing.Toast.ShareNsureAccountMissing, {
              advisorName: advisor ? getAgentFullName(advisor) : "",
            }),
          );
          return;
        }
        toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
      },
    },
  });

  const [advisorToShare, setAdvisorToShare] = useState<AgentSummary | null>(null);
  const [advisorToRemove, setAdvisorToRemove] = useState<AgentSummary | null>(null);
  const [isTakeBackConfirmOpen, setIsTakeBackConfirmOpen] = useState(false);

  const isMutating = isRemoving || isSharing;

  const handleShareConfirm = () => {
    if (!advisorToShare) return;
    shareAnalysis({ analysisId, versionId, data: { userGuid: advisorToShare.userGuid } }, { onSettled: () => setAdvisorToShare(null) });
  };

  const handleRemoveConfirm = () => {
    if (!advisorToRemove) return;
    removeCollaborator({ userGuid: advisorToRemove.userGuid }, { onSettled: () => setAdvisorToRemove(null) });
  };

  const shareDisabledReason = sharedWithAgent ? t(Resources.AnalysisDetail.Sharing.ShareDisabledTooltip) : undefined;

  const renderAdvisors = () => {
    if (isLoading) {
      return (
        <Stack spacing={Spacing.S}>
          <Skeleton variant="rounded" height={66} />
          <Skeleton variant="rounded" height={66} />
        </Stack>
      );
    }

    if (isError) {
      return <Alert variant="error" text={t(Resources.AnalysisDetail.Sharing.LoadError)} />;
    }

    if (advisors.length === 0) {
      return (
        <Empty
          title={t(Resources.AnalysisDetail.Sharing.NoAdvisors.Title)}
          text={t(Resources.AnalysisDetail.Sharing.NoAdvisors.Text)}
          icon={<PeopleAltOutlinedIcon sx={{ fontSize: "40px", color: "grey.500" }} />}
        />
      );
    }

    return (
      // minHeight 0 on both levels lets the list shrink below its content, so only the list scrolls when the modal hits maxHeight
      <Stack spacing={Spacing.S} minHeight={0}>
        <Stack spacing={Spacing.XS}>
          <SectionTitle>{t(Resources.AnalysisDetail.Sharing.SharedWith)}</SectionTitle>
          <Typography fontSize={12} color="text.secondary">
            {t(Resources.AnalysisDetail.Sharing.SingleShareNote)}
          </Typography>
        </Stack>
        <Stack divider={<Divider />} minHeight={0} overflow="auto" border={1} borderColor="divider" borderRadius="14px">
          {advisors.map((advisor) => (
            <SharingAdvisorItem
              key={advisor.userGuid}
              advisor={advisor}
              isShared={advisor.userGuid === sharedWithAgent?.userGuid}
              shareDisabledReason={shareDisabledReason}
              disabled={isMutating}
              onShare={() => setAdvisorToShare(advisor)}
              onRemove={() => setAdvisorToRemove(advisor)}
            />
          ))}
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <Modal open={open} onClose={onClose} aria-labelledby={titleId}>
        <Stack
          width={720}
          maxWidth="90vw"
          maxHeight="90vh"
          overflow="auto"
          spacing={Spacing.XXL}
          p={Spacing.XXL}
          bgcolor="common.white"
          boxShadow={3}
          borderRadius={Radius.XXXL}
        >
          <Flex justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography id={titleId} variant="h2">
                {t(Resources.AnalysisDetail.Sharing.Title)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(Resources.AnalysisDetail.Sharing.Subtitle, { analysisName, version })}
              </Typography>
            </Box>
            <IconButton aria-label={t(Resources.Common.Close)} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Flex>
          <Divider sx={{ mx: -Spacing.XXL }} />
          <Stack spacing={Spacing.S}>
            <SectionTitle>{t(Resources.AnalysisDetail.Sharing.CurrentlySharedWith)}</SectionTitle>
            <SharingCurrentAdvisor
              sharedWithAgent={sharedWithAgent}
              disabled={isMutating}
              onTakeBack={() => setIsTakeBackConfirmOpen(true)}
            />
          </Stack>
          <Stack spacing={Spacing.S}>
            <SectionTitle>{t(Resources.AnalysisDetail.Sharing.AddAdvisor.Title)}</SectionTitle>
            {advisors.length >= SHARING_ADVISORS_MAX_COUNT ? (
              <Alert
                variant="warning"
                text={t(Resources.AnalysisDetail.Sharing.AddAdvisor.LimitReached, { max: SHARING_ADVISORS_MAX_COUNT })}
              />
            ) : (
              <SharingAdvisorSearch addedAdvisorSsoIds={advisors.map(({ ssoId }) => ssoId)} />
            )}
          </Stack>
          {renderAdvisors()}
        </Stack>
      </Modal>
      <DialogModal
        size="sm"
        isOpen={!!advisorToShare}
        onClose={() => setAdvisorToShare(null)}
        isPending={isSharing}
        closeLabel={t(Resources.Common.Cancel)}
        title={t(Resources.AnalysisDetail.Sharing.Confirm.Share.Title)}
        subtitle={t(Resources.AnalysisDetail.Sharing.Confirm.Share.Text, {
          advisorName: advisorToShare ? getAgentFullName(advisorToShare) : "",
          advisorId: advisorToShare?.ssoId ?? "",
        })}
        primaryAction={
          <Button loading={isSharing} onClick={handleShareConfirm}>
            {t(Resources.AnalysisDetail.Sharing.Share)}
          </Button>
        }
      />
      <DialogModal
        size="sm"
        isOpen={!!advisorToRemove}
        onClose={() => setAdvisorToRemove(null)}
        isPending={isRemoving}
        closeLabel={t(Resources.Common.Cancel)}
        title={t(Resources.AnalysisDetail.Sharing.Confirm.Remove.Title)}
        subtitle={t(Resources.AnalysisDetail.Sharing.Confirm.Remove.Text, {
          advisorName: advisorToRemove ? getAgentFullName(advisorToRemove) : "",
          advisorId: advisorToRemove?.ssoId ?? "",
        })}
        primaryAction={
          <Button loading={isRemoving} onClick={handleRemoveConfirm}>
            {t(Resources.AnalysisDetail.Sharing.Confirm.Remove.Confirm)}
          </Button>
        }
      />
      <TakeBackConfirmDialog isOpen={isTakeBackConfirmOpen} advisor={sharedWithAgent} onClose={() => setIsTakeBackConfirmOpen(false)} />
    </>
  );
};
